
































































































































































































import { Component, Vue, PropSync, Ref } from 'vue-property-decorator';
import CompanyInvoiceSettingsForm from './CompanyInvoiceSettingsForm.vue';
import CompanyPaycheckSettingsForm from './CompanyPaycheckSettingsForm.vue';
import { unitInvoiceSettingFragments, unitPaycheckSettingFragments, unitSettingFragments, UNIT_INVOICE_SETTINGS, UNIT_PAYCHECK_SETTINGS } from '@/queries/CompanySetting';
import { GQL_saveUnitSetting } from '@/queries/__generated__/saveUnitSetting';
import { GQL_unitInvoiceSetting, GQL_unitInvoiceSetting_currentUnit } from '@/queries/__generated__/unitInvoiceSetting';
import { IRules } from '@/components/UI/Form/ValidationModule';
import SForm from '@/components/UI/Form/SForm.vue';
import { validateRequired } from '@/components/UI/Form/Validations';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { SmartQuery } from 'vue-apollo-decorator';
import DeepCopy from '@/decorators/deep-copy';
import { GQL_UnitSettingBase } from '@/queries/global';
import applicationModeService from '@/utils/application-mode-service';
import { GQL_unitPaycheckSetting } from '../../queries/__generated__/unitPaycheckSetting';
import formatDateTime from '@/filters/formatDateTime';
import gql from 'graphql-tag';

const SAVE_UNIT_SETTINGS = gql`
  mutation saveUnitSetting(
    $unitSetting: UnitSettingBase!,
  ) {
    saveUnitSetting(
      input: {
        unitSetting: $unitSetting
      }
    ) {
      unitSetting {
        ...unitSettingFragments
        unitInvoiceSetting {
          ...unitInvoiceSettingFragments
        }
        unitPaycheckSetting {
          ...unitPaycheckSettingFragments
        }
      }
    }
  },
  ${unitSettingFragments}
  ${unitInvoiceSettingFragments}
  ${unitPaycheckSettingFragments}
`;

@Component({
  components: {
    CompanyInvoiceSettingsForm,
    CompanyPaycheckSettingsForm,
  },
})
export default class CompanySettingsForm extends Vue {
  private applicationModeService = applicationModeService;

  @SmartQuery<CompanySettingsForm, GQL_unitInvoiceSetting | GQL_unitPaycheckSetting>({
    query: () => {
      return applicationModeService.currentMode === 'InvoicingMode'
        ? UNIT_INVOICE_SETTINGS
        : UNIT_PAYCHECK_SETTINGS;
    },
    fetchPolicy: "network-only",
    update(data) {
      const obj = {
        ...data.currentUnit,
        unitSetting: {
          ...data.currentUnit.unitSetting,
          __typename: undefined,
          id: undefined,
          unitId: data.currentUnit.id,
          oldLogos: undefined,
          unitInvoiceSetting: 'unitInvoiceSetting' in data.currentUnit.unitSetting
            ? {
              ...data.currentUnit.unitSetting.unitInvoiceSetting,
              __typename: undefined,
              id: undefined,
              customerCountry: undefined,
              customerCountryCode: data.currentUnit.unitSetting.unitInvoiceSetting!.customerCountry.code,
              displaySettings: {
                ...data.currentUnit.unitSetting.unitInvoiceSetting.displaySettings,
                __typename: undefined,
              },
            }
            : undefined,
          unitPaycheckSetting: 'unitPaycheckSetting' in data.currentUnit.unitSetting
            ? {
              ...data.currentUnit.unitSetting.unitPaycheckSetting,
              __typename: undefined,
              id: undefined,
              municipalityId: data.currentUnit.unitSetting.unitPaycheckSetting!.municipality?.id || '',
            }
            : undefined,
        },
      };

      if (applicationModeService.currentMode === 'InvoicingMode') delete obj.unitSetting.unitPaycheckSetting;
      if (applicationModeService.currentMode === 'PaycheckMode') delete obj.unitSetting.unitInvoiceSetting;

      return obj;
    },
    result() {
      // TODO: This is probably not what we want because we might lose changed data.
      // In case the application mode is changed, we reset the form dirty.
      this.resetFormDirty();
    },
  }) public currentUnit!: GQL_unitInvoiceSetting_currentUnit & { unitSetting: GQL_UnitSettingBase };

  @PropSync('dirty', { type: Boolean, default: false }) public dirtyForm!: boolean;

  @Ref() public readonly formRef?: SForm;

  @DeepCopy('currentUnit') private formData!: GQL_unitInvoiceSetting_currentUnit & { unitSetting: GQL_UnitSettingBase };

  public SAVE_UNIT_SETTINGS = SAVE_UNIT_SETTINGS;
  public validForm = true;
  public validInvoiceSettingForm = true;
  public validPaycheckSettingForm = true;

  public editLogo = false;
  public tempLogo: File | string = '';
  public newLogo: File | string | null = ''; // null means that logo should be deleted

  private formatDateTime = formatDateTime;

  public get logoImageSrc(): string | null {
    if (this.newLogo === null) return null; // if logo is for delete, do not display
    if (this.newLogo) return URL.createObjectURL(this.newLogo);
    return this.formData.unitSetting.logo.url;
  }

  public formatForMutation(data: GQL_UnitSettingBase): GQL_UnitSettingBase {
    return {
      ...data,
      // this.newLogo is '' if unchanged and causes error in backend.
      // we must send either logo to save it, null to remove it or undefined to ignore logo.
      logo: this.newLogo || (this.newLogo === null ? null : undefined),
      savedBy: undefined,
      savedAt: undefined,
      unitInvoiceSetting: 'unitInvoiceSetting' in data
        ? Object.assign({}, data.unitInvoiceSetting, {
          bban: undefined,
          iban: undefined,
          swift: undefined,
        })
        : undefined,
      unitPaycheckSetting: 'unitPaycheckSetting' in data
        ? Object.assign({}, data.unitPaycheckSetting, {
          municipality: undefined,
        })
        : undefined,
    } as GQL_UnitSettingBase;
  }

  private rules: IRules = {
    companyNameOption: [
      { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: i18n.t('Company.companyNameOption') }) },
      {
        validation: ({ dataValue }) => {
          if (dataValue !== 'ALIAS') return true;

          return !!this.currentUnit.unofficialName;
        },
      },
    ],
  };

  public saveUnitSettings(): Promise<boolean> {
    return this.$apollo.mutate({
      mutation: SAVE_UNIT_SETTINGS,
      variables: { unitSetting: this.formatForMutation(this.formData.unitSetting) },
    }).then(() => {
      this.unitSettingsAfterSaved();
      return true;
    }).catch(() => {
      return false;
    });
  }

  public unitSettingsSaved(store: InMemoryCache, query: {data: GQL_saveUnitSetting }): void {
    this.newLogo = '';

    try {
      const data = (store.readQuery({ query: UNIT_INVOICE_SETTINGS }) as GQL_unitInvoiceSetting);
      if (!data.currentUnit || !query.data.saveUnitSetting?.unitSetting) return;
      data.currentUnit.unitSetting = query.data.saveUnitSetting!.unitSetting!;
      store.writeQuery({ query: UNIT_INVOICE_SETTINGS, data });
    } catch (e) {}

    try {
      const data = (store.readQuery({ query: UNIT_PAYCHECK_SETTINGS }) as GQL_unitPaycheckSetting);
      if (!data.currentUnit || !query.data.saveUnitSetting?.unitSetting) return;
      data.currentUnit.unitSetting = query.data.saveUnitSetting!.unitSetting!;
      store.writeQuery({ query: UNIT_PAYCHECK_SETTINGS, data });
    } catch (e) {}

    this.unitSettingsAfterSaved();
  }

  private resetFormDirty(): void {
    this.$nextTick(() => {
      this.formRef && this.formRef.resetDirty();
    });
  }

  private unitSettingsAfterSaved(): void {
    this.$sMessage.success(this.$t('Company.Setting.saveSuccess'));
    this.resetFormDirty();
  }
}
